import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../lib/api";
import {
  resumeSession,
  submitAnswer,
  submitAudit,
  getQuestions,
} from "../lib/api/index";
import { logger } from "../lib/logger";

// NOTE: session_id is NEVER stored in localStorage. It lives in:
//  - The httpOnly signed cookie set by POST /api/audit/start (server-side)
//  - React state for the lifetime of this component
// On refresh, POST /api/audit/session/resume rehydrates from the cookie.

export default function Audit() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState({ current: 1, total: 12 });
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [textAnswer, setTextAnswer] = useState("");
  // Phase 5a — q1+Other follow-up text. Captured only when the active
  // question is q1_business_type AND the user has selected "Other".
  // Sent as `metadata.industry_other` on the /audit/answer POST.
  const [industryOther, setIndustryOther] = useState("");
  // P2 — snapshot pre-fill hints (set by /audit/session/resume). Used to
  // pre-fill q7 tools input + skip the legacy /audit/email step.
  const [toolsSnapshot, setToolsSnapshot] = useState("");
  const [snapshotDone, setSnapshotDone] = useState(false);
  // history: [{ question_id, answer }] — driven from server on resume
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Bootstrap: try cookie-based resume first; if no active session, start fresh.
  useEffect(() => {
    let cancelled = false;
    trackEvent("landing_view", "/audit");

    const bootstrap = async () => {
      try {
        const res = await resumeSession();
        if (cancelled) return;
        if (res.done) {
          // Already completed — server side has the cookie cleared.
          // Caller should navigate to the email step or preview; we route to email
          // so they can re-submit metadata if needed. (Email page will also resume.)
          navigate("/audit/email");
          return;
        }
        setSessionId(res.session_id);
        setQuestion(res.question);
        setProgress(res.progress);
        // P2 — capture snapshot hints for q7 pre-fill + post-Q12 routing.
        setToolsSnapshot(res.tools_snapshot || "");
        setSnapshotDone(Boolean(res.snapshot_completed_at));
        setHistory(
          (res.answers || []).map((a) => ({
            question_id: a.question_id,
            answer: a.answer,
          }))
        );
      } catch (err) {
        // No valid cookie or session — funnel into the P2 Snapshot flow
        // instead of silently minting a fresh audit. This makes /audit a
        // resilient direct-link target: a stale bookmark, deep link, or
        // any stray CTA that points at /audit lands the user in the
        // unified intake rather than skipping straight to Q1.
        if (err?.response?.status === 401) {
          if (!cancelled) navigate("/audit/snapshot", { replace: true });
        } else {
          logger.error("audit_resume_failed", { err: err?.response?.data || err?.message });
          setError("Could not load your audit. Please refresh and try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    bootstrap();
    return () => { cancelled = true; };
  }, [navigate]);

  // Pre-fill the input from history when the active question changes
  useEffect(() => {
    if (!question) return;
    const prior = history.find((h) => h.question_id === question.id);
    if (prior) {
      if (question.type === "single_choice") setSelectedAnswer(prior.answer);
      else setTextAnswer(prior.answer);
    } else if (question.id === "q7_tools" && toolsSnapshot) {
      // P2 — pre-fill q7 from the Snapshot tools text. User can confirm/edit.
      setTextAnswer(toolsSnapshot);
      setSelectedAnswer("");
    } else {
      setSelectedAnswer("");
      setTextAnswer("");
    }
    // Reset the q1-Other follow-up on every question change. (Re-priming
    // it from a prior session value would require an extra round-trip;
    // intentionally out of scope for 5a — most users land here once.)
    setIndustryOther("");
  }, [question, history, toolsSnapshot]);

  const submitAnswer = useCallback(async () => {
    if (!question || submitting) return;
    const answer = question.type === "single_choice" ? selectedAnswer : textAnswer.trim();
    if (!answer) {
      setError("Please provide an answer to continue.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Phase 5a — when the user is on q1 and picked "Other", attach the
      // optional industry follow-up under `metadata.industry_other`.
      // Empty / whitespace-only strings drop the metadata key entirely
      // (server treats absence and "" identically).
      const payload = {
        session_id: sessionId,
        question_id: question.id,
        answer,
      };
      if (
        question.id === "q1_business_type"
        && answer === "Other"
        && industryOther.trim()
      ) {
        payload.metadata = { industry_other: industryOther.trim() };
      }
      const res = await submitAnswer(payload);
      const nextHistory = [
        ...history.filter((h) => h.question_id !== question.id),
        { question_id: question.id, answer },
      ];
      setHistory(nextHistory);
      if (res.done) {
        // P2 — when the Snapshot step already captured intake fields,
        // submit directly and navigate to preview (skips the legacy
        // /audit/email intake page). Otherwise fall back to the email
        // step for users whose session pre-dates the Snapshot.
        if (snapshotDone) {
          try {
            await submitAudit({ session_id: sessionId });
            navigate(`/audit/preview/${sessionId}`);
          } catch (subErr) {
            logger.error("audit_post_q12_submit_failed", {
              err: subErr?.response?.data || subErr?.message,
            });
            navigate("/audit/email", { state: { sessionId } });
          }
        } else {
          // Pass session_id via in-memory router state. AuditIntake also calls
          // resume() as a safety net for page refresh.
          navigate("/audit/email", { state: { sessionId } });
        }
      } else {
        setQuestion(res.next_question);
        setProgress(res.progress);
        setSelectedAnswer("");
        setTextAnswer("");
      }
    } catch (err) {
      logger.error("audit_answer_failed", {
        question_id: question.id,
        err: err?.response?.data || err?.message,
      });
      setError("Failed to save your answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [question, submitting, selectedAnswer, textAnswer, industryOther, sessionId, history, navigate, snapshotDone]);

  // Backwards navigation: walk back through the in-memory progress.
  // The server retains all prior answers; if the user re-answers, the
  // existing record is overwritten by /api/audit/answer.
  // NOTE: depend on the primitive progress.current (not the object) so
  // eslint-plugin-react-hooks/exhaustive-deps is satisfied without
  // disable comments. setProgress always replaces the object reference.
  const currentStep = progress.current;
  const goBack = useCallback(async () => {
    if (!question || currentStep <= 1) return;
    try {
      const meta = await getQuestions();
      const qList = meta.questions;
      const newIndex = Math.max(0, currentStep - 2);
      const newQ = qList[newIndex];
      if (newQ) {
        setQuestion({ ...newQ, index: newIndex, total: qList.length });
        setProgress({ current: newIndex + 1, total: qList.length });
      }
    } catch (err) {
      logger.error("audit_back_navigation_failed", { err: err?.message });
      setError("Could not go back. Please try again.");
    }
  }, [question, currentStep]);

  const canSubmit =
    question &&
    (question.type === "single_choice"
      ? Boolean(selectedAnswer)
      : textAnswer.trim().length > 0);

  // Removes the nested ternary in the original render — easier to read,
  // easier to add new submit-button states later.
  const buttonLabel = (() => {
    if (submitting) return "Saving…";
    if (progress.current === progress.total) return "Continue";
    return "Next";
  })();

  if (loading) {
    return (
      <div className="sf-container" style={{ padding: "80px 24px" }} data-testid="audit-loading">
        <div className="sf-muted-text">Initializing diagnostic…</div>
      </div>
    );
  }

  if (error && !question) {
    return (
      <div className="sf-container" style={{ padding: "80px 24px" }} data-testid="audit-fatal-error">
        <div className="sf-card">
          <div className="sf-eyebrow" style={{ color: "var(--sf-gold)" }}>Audit unavailable</div>
          <p style={{ marginTop: 10 }}>{error}</p>
          <button className="sf-btn-secondary" onClick={() => window.location.reload()} data-testid="audit-fatal-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-container" style={{ padding: "48px 24px", maxWidth: 760 }} data-testid="audit-page">
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div className="sf-eyebrow" data-testid="audit-progress-label">
            Question {progress.current} of {progress.total}
          </div>
          <div className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)" }}>
            {Math.round((progress.current / progress.total) * 100)}%
          </div>
        </div>
        <div className="sf-progress-track" data-testid="audit-progress-bar">
          <div
            className="sf-progress-fill"
            style={{ width: `${(progress.current / progress.total) * 100}%` }}
          />
        </div>
      </div>

      <div className="sf-card" data-testid="audit-question-card">
        <h2
          className="sf-display"
          data-testid="audit-question-text"
          style={{ fontSize: 26, lineHeight: 1.25, marginBottom: 8 }}
        >
          {question.text}
        </h2>
        {question.subtext && (
          <p className="sf-muted-text" style={{ marginBottom: 24, fontSize: 14 }} data-testid="audit-question-subtext">
            {question.subtext}
          </p>
        )}

        {question.type === "single_choice" ? (
          <div data-testid="audit-choices" style={{ display: "grid", gap: 10 }}>
            {question.options.map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setSelectedAnswer(opt.label)}
                className={`sf-choice ${selectedAnswer === opt.label ? "is-selected" : ""}`}
                data-testid={`audit-choice-${opt.label.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
              >
                <span className="sf-choice-dot" />
                <span>{opt.label}</span>
              </button>
            ))}
            {/* Phase 5a — q1+Other industry follow-up. Renders only when the
                user has actually picked "Other" so the form doesn't feel
                interrogating. The text is optional — submission proceeds
                regardless of whether the user fills it in. */}
            {question.id === "q1_business_type" && selectedAnswer === "Other" && (
              <div
                style={{ marginTop: 14 }}
                data-testid="audit-q1-other-followup"
              >
                <label
                  htmlFor="audit-q1-other-input"
                  className="sf-mono"
                  style={{
                    display: "block", fontSize: 12, color: "var(--sf-muted)",
                    letterSpacing: "0.12em", textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  What industry are you in?
                </label>
                <input
                  id="audit-q1-other-input"
                  type="text"
                  className="sf-input"
                  value={industryOther}
                  onChange={(e) => setIndustryOther(e.target.value)}
                  placeholder="e.g. Landscaping, Cleaning, Plumbing, Roofing, Real Estate, Consulting, Coaching, Manufacturing…"
                  maxLength={80}
                  data-testid="audit-q1-other-input"
                />
                <div
                  className="sf-mono"
                  style={{ fontSize: 11, color: "var(--sf-muted)", marginTop: 6 }}
                >
                  Optional &mdash; helps us tailor your audit, but you can skip this.
                </div>
              </div>
            )}
          </div>
        ) : (
          <textarea
            className="sf-textarea"
            placeholder="Type your answer…"
            value={textAnswer}
            onChange={(e) => setTextAnswer(e.target.value)}
            data-testid="audit-text-input"
          />
        )}

        {error && (
          <div className="sf-mono" style={{ color: "#e2867e", fontSize: 13, marginTop: 16 }} data-testid="audit-inline-error">
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 28 }}>
          <button
            type="button"
            onClick={goBack}
            disabled={progress.current <= 1}
            className="sf-btn-secondary"
            data-testid="audit-back-btn"
          >
            Back
          </button>
          <button
            type="button"
            onClick={submitAnswer}
            disabled={!canSubmit || submitting}
            className="sf-btn"
            data-testid="audit-next-btn"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
